<template>
    <PageHeaderButtons :headerId="props.headerId"
        :buttons="buttons"
        :count="data.selectedItems?.length"
        :clearable="true"
        pluralizationCode="release"
        @clear="clearSelectedItems"
    />

    <div class="filters">
        <Field
            icon="search" sprite="actions"
            :placeholder="t('blocks.releases.search')"
            class="noerror light"
            @change="onSearchChange"
        />

        <Filters
            :filters="filters"
            @change="setFilters"
        />

        <Sort :sort="RELEASES_SORT"
            :code="data.sort?.code"
            @change="setSort" />
    </div>

    <div class="releases-list">
        <Explorer :items="data.items"
            @select="onSelectItems"
            @dblclick="openItem"
            itemWidth="158px"
            ref="explorer"
        >
            <template v-slot="{ item, selected }">
                <ReleasePreview :item="item"
                    :showState="true"
                    :disabled="props.disabledItems?.indexOf(item.id) !== -1"
                    v-context-menu="contextMenu"
                />
            </template>
        </Explorer>

        <LoadMore
            :loading="data.loading"
            :values="data.items"
            :total="data.total"
            :take="data.take"
            pluralization="release"
            @click="getItems"
        />

        <EmptyLabel
            v-if="!data.items.length && !data.loading"
            class="empty"
            :title="t(`pages.releases.empty.title`)"
            :description="t(`pages.releases.empty.description`)"
        />
    </div>
</template>

<script setup>
    import { useI18n } from 'vue-i18n';
    import { onMounted, reactive, ref, computed } from 'vue';
    // import api from '🔗/api';
    import Sort from '🔗/components/ui/Sort.vue';
    import Filters from '🔗/components/ui/Filters.vue';
    import Button from '🔗/components/ui/Button.vue';
    import LoadMore from '🔗/components/ui/LoadMore.vue';
    import Explorer from '🔗/components/ui/Explorer.vue';
    import EmptyLabel from '🔗/components/ui/EmptyLabel.vue';
    import ReleasePreview from '🔗/components/ui/ReleasePreview.vue';
    import Field from '🔗/components/ui/Field.vue';
    import KebabMenu from '🔗/components/ui/KebabMenu.vue';

    import PageHeaderButtons from '@/components/blocks/PageHeader/PageHeaderButtons.vue';

    // import stores from '@/stores';

    import { RELEASES_SORT, RELEASES_FILTERS } from '🔗/constants';


    const emit = defineEmits(['select', 'add', 'edit', 'editTracks', 'editTracksLicenses']);
    const explorer = ref(null);

    const releases = api.releases();
    const modals = stores.modals();
    const locale = stores.locale();

    const { t } = useI18n();

    const props = defineProps({
        artist: { type: [Object, null], default: null },
        sort: { type: Array, default: () => ([]) },
        filters: { type: Array, default: () => ([]) },

        disabledItems: { type: Array, default: () => ([]) },

        canAdd: { type: Boolean, default: true },
        canRemove: { type: Boolean, default: true },
        canEdit: { type: Boolean, default: true },
        canEditLicense: { type: Boolean, default: true },
        headerId: { type: [String, null], default: '#item-head-buttons' }
    });

    const data = reactive({
        items: [],
        loading: false,
        skip: 0,
        take: 30,
        total: 0,
        sort: null,
        searchStr: null,
        filters: {},

        addReleaseOptions: Array.from({ length: 10 }, (_, i) => ({
            code: i + 1,
            name: t('pluralization.releases.add', { count: i + 1, release: t('pluralization.releases.release', i + 1) })
        }))
    });

    const buttons = computed(() => {
        const result = [];

        if (!data.selectedItems?.length) {
            if (props.canAdd) {
                result.push({
                    type: 'kebab',
                    name: t(`pages.releases.add.title`),
                    icon: 'plus',
                    sprite: 'actions',
                    disabled: !releases.canAdd({ artist: props.artist }),
                    items: data.addReleaseOptions,
                    onClick: (item) => emit('add', item.code)
                })
            }

            return result;
        }

        if (data.selectedItems.some(release => releases.canRemove({
            artist: props.artist,
            release
        })) && props.canRemove) {
            result.push({
                type: 'button',
                title: data.selectedItems.length > 1 ? t(`pages.releases.remove-multiple.title`) : t(`pages.releases.remove.title`),
                icon: 'trash',
                sprite: 'actions',
                onClick: removeSelectedItems
            });
        }

        if (data.selectedItems.length === 1 && releases.canEditLicense({
            artist: props.artist,
            release: data.selectedItems[0]
        }) && props.canEditLicense) {
            result.push({
                type: 'button',
                name: t(`pages.releases.edit-tracks-licenses.title`),
                icon: 'license',
                sprite: 'sections',
                onClick: editItemLicenses
            });
        }

        if (data.selectedItems.length === 1 && props.canEdit) {
            result.push({
                type: 'button',
                name: t(`pages.releases.edit-track.title`),
                icon: 'note',
                sprite: 'player',
                disabled: !releases.canEditTracks({
                    artist: props.artist,
                    release: data.selectedItems[0]
                }),
                onClick: editItemTracks
            });
        }


        if (data.selectedItems.some(release => releases.canEdit({
                artist: props.artist,
                release
            }))) {
            result.push({
                type: 'button',
                name: data.selectedItems.length > 1 ? t(`pages.releases.edit-multiple.title`) : t(`pages.releases.edit.title`),
                icon: 'pen',
                sprite: 'actions',
                disabled: !props.canEdit,
                onClick: editSelectedItems
            });
        } else {
            result.push({
                type: 'button',
                name: data.selectedItems.length > 1 ? t(`pages.releases.view-multiple.title`) : t(`pages.releases.view.title`),
                icon: 'eye-opened',
                sprite: 'actions',
                disabled: !data.selectedItems.some(release => releases.canView({
                    artist: props.artist,
                    release
                })) || !props.canEdit,
                onClick: editSelectedItems
            });
        }

        return result;
    });

    const contextMenu = computed(() => {
        return buttons.value.filter(item => item.type === 'button').map(item => ({
            ...item,
            name: item.name || item.title
        })).reverse();
    });

    const filters = computed(() => {
        const conditions = [];

        if (props.artist?.id) {
            conditions.push((item) => item.code !== 'contributors');
        }

        if (!conditions?.length) {
            return RELEASES_FILTERS
        }

        return RELEASES_FILTERS.filter(item => !!conditions.every(condition => condition(item)));
    });

    const setFilters = (payload) => {
        data.filters = payload;
        reload();
    }

    const setSort = (item) => {
        if (data.sort?.code === item.code) {
            data.sort = null;
        } else {
            data.sort = item;
        }

        reload();
    };

    const onSearchChange = async (e) => {
        data.searchStr = e.target.value?.toLowerCase().trim() || '';
        data.skip = 0;

        clearTimeout(data.searchTimeout);

        data.searchTimeout = setTimeout(() => {
            delete data.searchTimeout;
            reload();
        }, 200);
    }

    const getItems = async () => {
        data.loading = true;
        
        clearTimeout(data.setItemsTimeout);

        if (!data.skip) {
            data.items = [];
        }

        let params = {
            skip: data.skip,
            take: data.take
        };

        if (data.sort) {
            params.sort = data.sort.code;
            params.sort_dir = data.sort.direction;
        }

        if (!!data.searchStr?.trim().length) {
            params.title = data.searchStr.toLowerCase().trim();
        }

        if (data.filters && Object.keys(data.filters)?.length) {
            params = { ...data.filters, ...params };
        }

        if (props.artist) {
            params.contributors = [props.artist.id].join(',')
        }

        const payload = await api.releases().list(params) || [];

        data.setItemsTimeout = setTimeout(() => {
            if (payload?.items?.length) {
                data.items.splice(data.items.length, 0, ...payload.items);
            }

            data.skip = data.items.length;
            data.total = payload?.total || 0;

            data.loading = false;
        }, 200);
    };

    const reload = () => {
        data.skip = 0;
        getItems();
    };

    const addItem = (item) => {
        if (!item?.id) {
            return;
        }

        data.items.unshift(item)
        data.skip += 1;
        data.total += 1;
    }

    const updateItem = (payload) => {
        if (!payload?.id) {
            return;
        }

        const item = data.items.find(item => item.id === payload.id);
        if (item) {
            data.items.splice(data.items.indexOf(item), 1, payload);
        }
    }

    const removeItems = async (items, happened, with_tracks) => {
        if (!items?.length) {
            return;
        }

        // Удаляем лишние
        if (!happened) {
            await Promise.all(items.map(async (item) => {
                await releases.remove(item.id, with_tracks);
            }));
        }

        const ids = items.map(item => item.id);

        const oldlength = data.items.length;
        data.items = data.items.filter(item => ids.indexOf(item.id) === -1);

        if (oldlength - data.items?.length > 0) {
            data.skip -= oldlength - data.items?.length;
            data.total -= oldlength - data.items?.length;
        }
    }

    const onSelectItems = (items) => {
        data.selectedItems = items;
        emit('select', items)
    }

    const openItem = (item) => {
        if (!item?.id) {
            return;
        }

        modals.reopen('release', {
            data: item.id,
            popover: true
        })
    }

    const removeSelectedItems = async () => {
        if (!data.selectedItems?.length) {
            return;
        }

        modals.confirm({
            title: t(`pages.releases.remove-multiple.confirm.title`),
            body: t(`pages.releases.remove-multiple.confirm.description`),
            additionFields: [
                {
                    name: t(`pages.releases.remove-multiple.confirm.with-tracks`),
                    checked: false
                }
            ],
            onOk: async ({ additionFields }) => {
                const with_tracks = additionFields?.length ? !!additionFields[0].checked : false;
                await removeItems(data.selectedItems.filter(release => releases.canRemove({
                    artist: props.artist,
                    release
                })), null, with_tracks);
                
                modals.open('success', {
                    data: {
                        title: t(`pages.releases.remove-multiple.done.title`),
                        close: t(`pages.releases.remove-multiple.done.close`)
                    },
                    quietClose: true
                });

                clearSelectedItems();
            }
        }); 
    }

    const editSelectedItems = () => {
        if (!data.selectedItems?.length) {
            return;
        }

        emit('edit', data.selectedItems);
    }

    const editItemTracks = () => {
        if (data.selectedItems?.length !== 1) {
            return;
        }

        emit('editTracks', data.selectedItems[0]);
    }

    const editItemLicenses = () => {
        if (data.selectedItems?.length !== 1) {
            return;
        }

        emit('editTracksLicenses', data.selectedItems[0]);
    }

    const clearSelectedItems = () => {
        explorer?.value?.clearSelection();
        delete data.selectedItems;
        emit('select')
    }

    defineExpose({
        reload,
        updateItem,
        removeItems,
        addItem
    });

    onMounted(() => {
        getItems();
    });
</script>

<style lang="scss" scoped>
    .releases-list {
        display: flex;
        flex: 1 1;
        flex-direction: column;

        .loader {
            flex-shrink: 0;
            margin: auto;
        }

        button {
            flex-shrink: 0;
        }
    }

    .filters {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        justify-content: end;
        padding: 0 10px;
    }
</style>
